import React, { useCallback, useEffect, useRef } from 'react';
import http from 'utils/http';
import Spacer from '../../Spacer/Spacer';
import AutocompleteAdapter from './AutocompleteAdapter';
import { AdapterProps } from '../types';
import { useQuery } from '@tanstack/react-query';
import Skeleton from '@mui/material/Skeleton';
import { Stack } from '@mui/material';
import debounce from 'lodash/debounce';
import { Pagination } from '../../../../modules/@common/crud/types';
import { useFormContext, useWatch, UseFormSetValue } from 'react-hook-form';

export type ServerAutocompleteAdapterProps =
  AdapterProps['server-autocomplete'] &
    Omit<AdapterProps['autocomplete'], 'options'>;

type ServerResource = {
  id: number;
  name: string;
};

type ServerResponse = ServerResource[] | Pagination<ServerResource>;

function isPaginationResponse(
  response: ServerResponse,
): response is Pagination<ServerResource> {
  return (
    response.hasOwnProperty('data') &&
    Array.isArray((response as Pagination<ServerResource>).data)
  );
}

type AutocompleteOption = { value: number; label: string };

function optionIdsEqual(a: unknown, b: unknown): boolean {
  return Number(a) === Number(b);
}

function findServerOptionForValue(
  options: AutocompleteOption[],
  id: unknown,
): AutocompleteOption | undefined {
  return options.find((o) => optionIdsEqual(o.value, id));
}

/**
 * When the form holds `{ value: id, label: '' }` from defaults, replace it with the
 * matching option from the latest server fetch so the control shows `name`.
 */
function syncFieldValueWithServerOptions(
  name: string,
  rawValue: unknown,
  options: AutocompleteOption[],
  multiple: boolean | undefined,
  setValue: UseFormSetValue<any>,
): void {
  if (!options.length) {
    return;
  }

  if (multiple) {
    if (!Array.isArray(rawValue) || rawValue.length === 0) {
      return;
    }
    let changed = false;
    const next = rawValue.map((entry: AutocompleteOption) => {
      const match = findServerOptionForValue(options, entry?.value);
      if (match && entry.label !== match.label) {
        changed = true;
        return match;
      }
      return entry;
    });
    if (changed) {
      setValue(name, next, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      });
    }
    return;
  }

  if (
    rawValue == null ||
    typeof rawValue !== 'object' ||
    !('value' in (rawValue as object))
  ) {
    return;
  }

  const current = rawValue as AutocompleteOption;
  const match = findServerOptionForValue(options, current.value);
  if (!match || current.label === match.label) {
    return;
  }

  setValue(name, match, {
    shouldDirty: false,
    shouldTouch: false,
    shouldValidate: false,
  });
}

const ServerAutocompleteAdapter = ({
  optionsUrl,
  name,
  multiple,
  jsonParams,
  ...props
}: ServerAutocompleteAdapterProps) => {
  const inputValueRef = useRef('');
  const { setValue } = useFormContext();
  const rawValue = useWatch({ name });

  const {
    data = [],
    isLoading,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: [
      optionsUrl,
      'server-autocomplete-adapter-options',
      props.params || {},
      jsonParams,
    ],
    queryFn: async () => {
      const query = inputValueRef.current;
      const extraParams = props.params || {};
      const requestParams = jsonParams
        ? {
            params: JSON.stringify({
              ...extraParams,
              query,
            }),
          }
        : {
            ...extraParams,
            query,
          };

      let items = await http
        .get<any, ServerResponse>(optionsUrl, {
          params: requestParams,
        })
        .then((response) => {
          if (isPaginationResponse(response)) {
            return response.data;
          }

          return response;
        });

      return items.map((item) => ({
        value: item.id,
        label: item.name,
      }));
    },
  });

  useEffect(() => {
    syncFieldValueWithServerOptions(name, rawValue, data, multiple, setValue);
  }, [name, rawValue, data, multiple, setValue]);

  const debouncedRefetch = useCallback(
    debounce(() => {
      void refetch();
    }, 250),
    [refetch],
  );

  if (isLoading) {
    return (
      <Stack direction="column" flex="1">
        <Skeleton width="100%" height={16} variant="rectangular" />
        <Spacer size={1} />
        <Skeleton width="100%" height={54} variant="rectangular" />
      </Stack>
    );
  }

  return (
    <AutocompleteAdapter
      {...props}
      name={name}
      multiple={multiple}
      options={data}
      isLoading={isFetching}
      clearOnBlur={false}
      onInputChange={(event, value, reason) => {
        inputValueRef.current = value;
        debouncedRefetch();
      }}
    />
  );
};

export default ServerAutocompleteAdapter;
